import { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip, Circle, ZoomControl, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { CITY_COORDS } from "@/lib/data";
import { useI18n } from "@/i18n";

const MAP_STYLES = {
  street: {
    url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
    maxZoom: 19,
  },
  aerial: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    maxZoom: 19,
  },
};

function FlyTo({ focus }) {
  const map = useMap();
  useEffect(() => {
    if (focus) map.flyTo([focus.lat, focus.lng], 13, { duration: 1.1 });
  }, [focus, map]);
  return null;
}

export default function CoverageMap({ focus }) {
  const { t } = useI18n();
  const [mapStyle, setMapStyle] = useState("street");
  const activeStyle = MAP_STYLES[mapStyle];

  return (
    <div className="border-2 border-[#0B0F14] bg-[#FBFAF8] shadow-[3px_3px_0_#0E3B3B] sm:shadow-[5px_5px_0_#0E3B3B]" data-testid="coverage-map">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b-2 border-[#0B0F14] bg-[#0E3B3B] px-3 py-2.5 text-white sm:px-4" data-testid="coverage-map-header">
        <div className="flex min-w-0 items-center gap-2">
          <span className="h-2.5 w-2.5 shrink-0 bg-emerald-400 shadow-[0_0_0_3px_rgba(52,211,153,0.18)]" />
          <span className="truncate font-mono-tech text-[10px] font-bold uppercase tracking-[0.22em] sm:text-[11px]">
            {t("sa.plate")} / {t("sa.countyLine")}
          </span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <span className="hidden font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/60 lg:inline">
            {t("sa.coords")}
          </span>
          <div className="flex border border-white/25" data-testid="coverage-map-style-toggle">
            {Object.keys(MAP_STYLES).map((style) => (
              <button
                key={style}
                type="button"
                onClick={() => setMapStyle(style)}
                aria-pressed={mapStyle === style}
                data-testid={`coverage-map-style-${style}`}
                className={`px-2.5 py-1 font-mono-tech text-[10px] font-bold uppercase tracking-widest transition-colors ${mapStyle === style ? "bg-white text-[#0E3B3B]" : "text-white/70 hover:bg-white/10 hover:text-white"}`}
              >
                {t(`sa.${style}`)}
              </button>
            ))}
          </div>
          <span className="bg-emerald-400 px-2 py-1 font-mono-tech text-[10px] font-bold uppercase tracking-widest text-[#0A2C2C]" data-testid="coverage-map-city-count">
            {CITY_COORDS.length} {t("sa.areasWord")}
          </span>
        </div>
      </div>

      <div className={`ews-map relative h-[440px] sm:h-[520px] ${mapStyle === "street" ? "ews-map-muted" : ""}`}>
        <MapContainer center={[25.87, -80.2]} zoom={10} scrollWheelZoom={false} zoomControl={false} attributionControl={false} style={{ height: "100%", width: "100%" }}>
          <TileLayer key={mapStyle} url={activeStyle.url} maxZoom={activeStyle.maxZoom} />
          <Circle center={[25.87, -80.2]} radius={17000} pathOptions={{ color: "#0E3B3B", fillColor: "#059669", fillOpacity: 0.07, weight: 2, dashArray: "8 8" }} />
          {focus && (
            <Circle center={[focus.lat, focus.lng]} radius={1200} pathOptions={{ color: "#F59E0B", fillColor: "#F59E0B", fillOpacity: 0.08, weight: 2, dashArray: "4 6" }} />
          )}
          {CITY_COORDS.map((c) => {
            const isFocus = focus && focus.name === c.name;
            return (
              <CircleMarker
                key={c.name}
                center={[c.lat, c.lng]}
                radius={isFocus ? 11 : 7}
                pathOptions={{ color: isFocus ? "#0B0F14" : "#0E3B3B", fillColor: isFocus ? "#F59E0B" : "#059669", fillOpacity: 0.95, weight: isFocus ? 3 : 2 }}
              >
                <Tooltip direction="top" offset={[0, -4]} className="ews-map-tooltip">{c.name}</Tooltip>
              </CircleMarker>
            );
          })}
          <ZoomControl position="bottomright" />
          <FlyTo focus={focus} />
        </MapContainer>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t-2 border-[#0B0F14] bg-[#F0EFEA] px-3 py-2 font-mono-tech text-[9px] uppercase tracking-[0.18em] text-slate-600 sm:px-4 sm:text-[10px]" data-testid="coverage-map-footer">
        <span>{t("sa.mapFoot")}</span>
        <span>{t("sa.coverageFoot")}</span>
      </div>
    </div>
  );
}
